import React from "react";
import PageShell from "@/components/PageShell";
import { Sparkles, BookOpen } from "lucide-react";

// Biography paragraphs — kept verbatim as supplied by the user.
const BIO = [
  'أنا الخالدي، كاتبة ومدربة في مجال تطوير الذات والعلاقات، حاصلة على شهادة متخصصة في الإرشاد النفسي، وتمتلك خبرة تتجاوز عشر سنوات في إدارة الموهوبات وتطوير البرامج الإثرائية وتنمية مهارات التفكير وبناء الذات.',
  'تتمتع أنا الخالدي، كاتبة ومؤلفة كتاب "ثبتي أوتادك" بمسار مهني ملهم وبخبرة غنية تزيد عن 10 سنوات، واستنادًا إلى خبرتها الواسعة، قادت أنا الخالدي العديد من البرامج الإثرائية المبتكرة التي تستهدف معالجة المشكلات بطرق إبداعية وتفعيل مهارات التفكير وبناء الذات. كما قامت بتطوير مجموعة من الأدوات والاستراتيجيات التي تهدف إلى تحسين جودة العلاقات الشخصية والعاطفية والاجتماعية. مما أسهم في إثراء مجتمعها بطرق ملموسة وجعلها مرجعًا في مجالها.',
  "أنا الخالدي حاصلة على العديد من الدورات والشهادات المعتمدة في مجالات مثل بناء الثقة بالنفس، وتطوير المهارات الشخصية، وفنون التواصل الفعّال. ومن بينها دبلوم البرمجة اللغوية العصبية NLP والنموذج الأمثل الشامل للمدرسة وحل المشكلات بطريقة إبداعية CBS، إلى جانب دورات أخرى تتعلق بتطوير المهارات القيادية وبناء الذات.",
  'تبرز أنا الخالدي في مجال تحفيز الآخرين وتوجيههم نحو تحقيق أهدافهم الشخصية. وباعتبارها مؤلفة كتاب "ثبتي أوتادك"، تعكس أنا الخالدي رؤية ومهارات عميقة في بناء العلاقات الناجحة وتعزيز التواصل الإيجابي بين الأفراد.',
  'وبفضل رؤيتها الفريدة وتفانيها في تقديم النصائح والإرشادات، تسعى جاهدة لمساعدة الأفراد على تعزيز علاقاتهم وتحقيق النجاح والرضا في حياتهم الشخصية وذلك من خلال كتاب "ثبتي أوتادك" الذي يعكس رؤية المؤلفة حول بناء العلاقات الناجحة. ويقدم للقرّاء استراتيجيات وأدوات فعّالة لتعزيز العلاقات الإيجابية في حياتهم الشخصية. كما يقدم "ثبتي أوتادك" نصائح قيّمة حول كيفية بناء علاقات متينة ومثمرة. فهو يركز على أهمية التواصل الفعّال وفن التعامل مع الآخرين بأسلوب محترم ومتعاون، مما يساعد في تعزيز التفاهم وبناء الثقة وتحقيق النجاح المشترك. كما يُعتبر "ثبتي أوتادك" دليلًا مهمًا لكل من يسعى لبناء علاقات ناجحة ومثمرة في حياته الشخصية.',
];

// Credentials list — top-level bullets + nested course bullets.
const CREDENTIALS = [
  { text: "شهادة متخصصة في الإرشاد النفسي." },
  { text: "خبرة تتجاوز 10 سنوات في إدارة الموهوبات" },
  {
    text:
      "تنفيذ وإشراف على العديد من البرامج الإثرائية في مجال معالجة المشكلات بطرق إبداعية وتفعيل مهارات التفكير وبناء الذات",
  },
  { text: "مطورة لعدد من البرامج في إدارة الحياة ومعالجة المشكلات" },
  {
    text: "حاصلة على العديد من الدورات:",
    children: [
      "دبلوم البرمجة اللغوية العصبية NLP",
      "School Wide Optimum Model — النموذج الأمثل الشامل لكل المدرسة",
      "حل المشكلات بطريقة إبداعية CBS",
      "الامتياز كمعلم معتمد SWOM في دمج مهارات التفكير في المناهج التعليمية",
      "سوام SWOM التأسيسية لدمج مهارات التفكير في المناهج",
      "أفكار إبداعية في حفظ القرآن الكريم",
      "مهارات تنمية التفكير كورت 1",
      "مهارات تنمية التفكير كورت 2",
      "مهارات تنمية التفكير كورت 3",
      "أدوات الجودة الشاملة في التعليم",
      "تنمية مهارات القيادة النسائية",
      "بناء تقدير الذات",
    ],
  },
];

const About = () => (
  <PageShell>
    <section
      className="max-w-3xl mx-auto px-6 lg:px-12 py-16"
      data-testid="about-page"
    >
      {/* Top pill badge — matches Terms / PrivacyRefund pages */}
      <div className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full bg-[#F6F4EE] border border-[#E5E1D8] text-[#0A4D3C] text-sm">
        <Sparkles className="w-4 h-4" /> عن المؤلفة
      </div>
      <h1 className="font-heading text-4xl sm:text-5xl text-[#222222] mt-6">
        عن المؤلفة
      </h1>

      {/* Biography card */}
      <div
        className="mt-10 rounded-2xl border border-[#E5E1D8] bg-[#FAF9F6] p-6 sm:p-8"
        data-testid="about-bio-card"
      >
        <h2
          className="font-heading text-2xl text-[#222222]"
          data-testid="about-bio-heading"
        >
          عن المؤلفة:
        </h2>
        <div className="mt-5 space-y-5 text-[#222222] leading-loose text-base">
          {BIO.map((p, i) => (
            <p key={i} data-testid={`about-bio-paragraph-${i + 1}`}>
              {p}
            </p>
          ))}
        </div>
      </div>

      {/* Credentials card */}
      <div
        className="mt-8 rounded-2xl border border-[#E5E1D8] bg-[#FAF9F6] p-6 sm:p-8"
        data-testid="about-credentials-card"
      >
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-[#F6F4EE] grid place-items-center text-[#0A4D3C]">
            <BookOpen className="w-5 h-5" />
          </span>
          <h2
            className="font-heading text-2xl text-[#222222]"
            data-testid="about-credentials-heading"
          >
            الخبرات والشهادات:
          </h2>
        </div>

        <ul className="mt-6 list-disc ps-6 space-y-3 text-[#222222] leading-loose text-base">
          {CREDENTIALS.map((item, i) => (
            <li
              key={i}
              data-testid={`about-credential-${i + 1}`}
              className="ms-2"
            >
              {item.text}
              {item.children && (
                <ul className="mt-3 ps-4 space-y-2 text-[#5E5C58]">
                  {item.children.map((c, j) => (
                    <li
                      key={j}
                      data-testid={`about-credential-${i + 1}-${j + 1}`}
                      className="relative ps-4 leading-loose"
                    >
                      <span
                        aria-hidden="true"
                        className="absolute end-0 top-[14px] w-1.5 h-1.5 rounded-full bg-[#0A4D3C]"
                      />
                      {c}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  </PageShell>
);

export default About;
